import React, { useCallback, useEffect, useRef, useState } from 'react';
import i18n from 'i18next';
import { toast } from 'react-toastify';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import PropTypes from 'prop-types';
import Select from '@material-ui/core/Select';
import { MenuItem } from '@material-ui/core';
import { useModal } from 'react-modal-hook';
import useStyles from './PatrolList.styles';
import useAgent from '../../../common/hooks/useAgent';
import { Table } from '../../../table/components';
import { defaultTake, fullPageTableHeight } from '../../../table/components/Table';
import patrolAgent from '../../agents/patrolAgent';
import { formatDate, formatSecondsDuration } from '../../../common/formatters/timeFormatters';
import UploadButton from '../../../common/components/buttons/UploadButton';
import { inputToFormData } from '../../../common/utils/fileMethods';
import { Button } from '../../../common/components/buttons';
import { Container } from '../../../common/components/layout';
import usePaginatedAgent from '../../../common/hooks/usePaginatedAgent';
import { ContainerLoader, Loader } from '../../../common/components/loaders';
import ConfirmationModal from '../../../common/components/modals/ConfirmationModal';
import { useAbilityContext } from '../../../common/contexts/abilityContext';
import TransferredIcon from './columns/TransferredIcon';
import PilotNameColumn from './columns/PilotNameColumn';
import exportFilteredPatrols from '../../utils/exportFilteredPatrols';
import clientAgent from '../../../client/agents/clientAgent';

const formatMissionName = (missionName, isMissionDeleted) => {
  if (isMissionDeleted) {
    return `${missionName} (${i18n.t('patrol.deleted')})`;
  }
  return missionName;
};

const PatrolList = ({ onRowClick }) => {
  const classes = useStyles();
  const context = useAbilityContext();

  const [selectedPatrolToDelete, setSelectedPatrolToDelete] = useState();
  const [fetchPatrols, patrols] = usePaginatedAgent(patrolAgent.fetchPatrols);
  const [uploadPatrol, uploadedPatrol, isUploadingPatrol] = useAgent(patrolAgent.uploadPatrol);
  const [deletePatrol, deletedPatrol] = useAgent(patrolAgent.deletePatrol);
  const [fetchClients, clients] = useAgent(clientAgent.fetchClients);
  const canManageAll = context.can('manage', 'all');

  useEffect(() => {
    if (canManageAll) {
      fetchClients({ orderBy: 'name asc' });
    }
  }, [canManageAll, fetchClients]);

  const history = useHistory();

  const { url } = useRouteMatch();

  const [showModal, hideModal] = useModal(
    () => (
      <ConfirmationModal
        closeModal={hideModal}
        onCancelClick={() => setSelectedPatrolToDelete(undefined)}
        onConfirmClick={() => deletePatrol(selectedPatrolToDelete.id)}
        confirmButtonLabel={i18n.t('common.button.confirm')}>
        {i18n.t('patrol.message.deletionConfirmation')}
      </ConfirmationModal>
    ),
    [selectedPatrolToDelete]
  );

  const handleDeleteClick = useCallback((event) => {
    setSelectedPatrolToDelete(event);
  }, []);

  useEffect(() => {
    fetchPatrols({ take: defaultTake, orderBy: 'receivedAt desc' });
  }, [fetchPatrols]);

  useEffect(() => {
    if (uploadedPatrol) {
      history.push(`${url}/${uploadedPatrol.id}`);
      toast.success(i18n.t('common.message.success.uploaded', { entity: 'report' }));
    }
  }, [history, uploadedPatrol, url]);

  useEffect(() => {
    if (deletedPatrol) {
      toast.success(i18n.t('common.message.success.deleted', { entity: 'report' }));
      fetchPatrols({ take: defaultTake });
    }
  }, [deletedPatrol, fetchPatrols]);

  useEffect(() => {
    if (selectedPatrolToDelete) {
      showModal();
    }
  }, [selectedPatrolToDelete, showModal]);

  useEffect(() => {
    if (uploadedPatrol?.pilotFound === false) {
      toast.warning(i18n.t('patrol.message.missionPilotNotFound'));
    }
  }, [uploadedPatrol]);

  const defaultColumns = [
    {
      Header: i18n.t('patrol.id'),
      accessor: 'id',
      columnWidth: 4,
    },
    {
      id: 'isTransferred',
      Header: '',
      accessor: TransferredIcon,
    },
    {
      Header: i18n.t('patrol.table.missionName'),
      accessor: ({ missionName, isMissionDeleted }) => formatMissionName(missionName, isMissionDeleted),
    },
    {
      Header: i18n.t('patrol.table.patrolStartDate'),
      accessor: ({ startedAt, startedAtTimezone }) => formatDate(startedAt, startedAtTimezone),
      columnWidth: 10,
    },
    {
      Header: i18n.t('patrol.table.receivedDate'),
      accessor: ({ receivedAt, receivedAtTimezone }) => formatDate(receivedAt, receivedAtTimezone),
      columnWidth: 10,
    },
    {
      Header: i18n.t('patrol.table.coverage'),
      accessor: ({ coveragePercentage }) => `${(coveragePercentage * 100).toFixed(2)} %`,
      columnWidth: 4,
    },
    { Header: i18n.t('patrol.table.detectedThreats'), accessor: 'detectedThreatsCount', columnWidth: 1 },
    { Header: i18n.t('patrol.finding.findings'), accessor: 'findingsCount', columnWidth: 1 },
    {
      Header: i18n.t('patrol.table.pilotName'),
      accessor: PilotNameColumn,
    },
    { Header: i18n.t('patrol.table.pilotLicenseNumber'), accessor: 'pilotLicenseNumber' },
    {
      Header: i18n.t('patrol.table.flightDuration'),
      accessor: ({ flightDurationInSeconds }) => formatSecondsDuration(flightDurationInSeconds),
    },
    {
      Header: i18n.t('patrol.table.inspectionDuration'),
      accessor: ({ inspectionDurationInSeconds }) => formatSecondsDuration(inspectionDurationInSeconds),
    },
  ];

  const { current: columns } = useRef(
    context.can('create', 'client')
      ? defaultColumns.concat([
          {
            Header: i18n.t('common.entity.client'),
            accessor: 'clientName',
          },
        ])
      : defaultColumns
  );

  const { current: rowActions } = useRef(
    context.can('delete', 'patrol')
      ? [
          {
            icon: 'delete',
            onClick: handleDeleteClick,
          },
        ]
      : []
  );

  const handleUploadClick = useCallback(
    (callback) => {
      uploadPatrol(inputToFormData(callback, 'request'));
    },
    [uploadPatrol]
  );

  const handlePageChange = useCallback(
    ({ take, skip }) =>
      fetchPatrols({
        take,
        skip,
        orderBy: 'receivedAt desc',
      }),
    [fetchPatrols]
  );

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(0);

  const handleStartDateChange = (date) => {
    if (date === null) {
      setStartDate(null);
      return;
    }
    setStartDate(moment.utc(date).startOf('day'));
  };

  const handleClientChange = (client) => {
    setSelectedClientId(parseInt(client.target.value, 10));
  };

  const handleEndDateChange = (date) => {
    if (date === null) {
      setEndDate(null);
      return;
    }

    setEndDate(date || moment.utc(date).endOf('day'));
  };

  const handleFilterClick = useCallback(() => {
    fetchPatrols({
      take: defaultTake,
      orderBy: 'receivedAt desc',
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      clientId: selectedClientId,
    });
  }, [fetchPatrols, startDate, endDate, selectedClientId]);

  if (!patrols || (canManageAll && !clients)) return <ContainerLoader />;

  return (
    <Container>
      <div className={classes.buttonContainer}>
        {canManageAll && (
          <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
            <Select className={classes.clientSelector} value={selectedClientId} defaultValue="" required displayEmpty onChange={handleClientChange}>
              <MenuItem value="0">
                <span className={classes.clientSelectorLabel}>{i18n.t('patrol.filter.selectClient')}</span>
              </MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
            <KeyboardDatePicker
              autoOk
              showTodayButton
              value={startDate}
              format="D MMM, YYYY"
              onChange={handleStartDateChange}
              maxDate={endDate || moment()}
              label={i18n.t('patrol.button.startDate')}
            />
            <KeyboardDatePicker
              autoOk
              showTodayButton
              value={endDate}
              format="D MMM, YYYY"
              onChange={handleEndDateChange}
              minDate={startDate || moment().subtract(6, 'months')}
              maxDate={moment()}
              label={i18n.t('patrol.button.endDate')}
            />
            <Button primary onClick={handleFilterClick} className={classes.filterButton}>
              {i18n.t('patrol.button.filterPatrols')}
            </Button>
            <Button primary onClick={() => exportFilteredPatrols(patrols, startDate, endDate)} disabled={!startDate || !endDate} className={classes.button}>
              {i18n.t('patrol.button.exportPatrols')}
            </Button>
          </MuiPickersUtilsProvider>
        )}
        {isUploadingPatrol && <Loader className={classes.loader} size={30} />}
        <UploadButton onClick={handleUploadClick} type=".json">
          <Button component="span" primary disabled={isUploadingPatrol}>
            {i18n.t('patrol.button.uploadReport')}
          </Button>
        </UploadButton>
      </div>
      <div className={classes.table}>
        <Table columns={columns} data={patrols} height={fullPageTableHeight} rowActions={rowActions} onPageChange={handlePageChange} onRowClick={onRowClick} />
      </div>
    </Container>
  );
};

PatrolList.propTypes = {
  onRowClick: PropTypes.func,
};

PatrolList.defaultProps = {
  onRowClick: undefined,
};

export default PatrolList;
